/* eslint-disable react/jsx-max-depth */
/* eslint-disable max-lines-per-function */
import { ReactElement } from 'react';

import { Visit, VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useDataSyncing } from '@dreryk/command-query-react-common';
import { isFault } from '@dreryk/command-query-ws-client';
import {
   ActionBar,
   ActionBarElementType,
   DataSyncWaitingEventInterceptor,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { PromiseVoidFunction } from '@dreryk/react-common';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';
import { SentPrescriptionPackage } from '~libs.shared.prescription';

import { draftPrescriptionsTranslationKeys } from '../../../../translations';
import { PatientId, VisitId } from '../../PrescriptionIssuingPanel.types';
import { OnPrescriptionsSent } from '../prescriptions.types';

import { useDraftPrescriptionsWatch } from './DraftPrescriptionsForm.hooks';
import { DraftPrescriptionsFormController } from './DraftPrescriptionsForm.types';
import { DraftPrescriptionsStatusPanelPortal } from './DraftPrescriptionsStatusPanel';
import { NfzDepartmentUpdateEventInterceptor } from './nfz/NfzDepartmentUpdateEventInterceptor';
import { PatientEntitlementBenefitsCheckEventInterceptor } from './patientEntitlements/patientEntitlementsBenefitsCheck/PatientEntitlementBenefitsCheckEventInterceptor';
import { PatientEntitlementsEventInterceptor } from './patientEntitlements/PatientEntitlementsEventInterceptor';
import { PatientReimbursementIndicationsCheckEventInterceptor } from './patientEntitlements/patientReimbursementIndicationsCheck/PatientReimbursementIndicationsCheckEventInterceptor';
import { usePatientPrescriptionsHistoryDownloadVerificationModal } from './PatientPrescriptionsHistoryDownloadVerificationModal/PatientPrescriptionsHistoryDownloadVerificationModal.hooks';
import { useProAuctoreAndProFamiliaMissingContactDetailsModalOpener } from './ProAuctoreAndProFamiliaMissingContactDetailsModal/ProAuctoreAndProFamiliaMissingContactDetailsModal.hooks';
import { useCqrsVisitPrescriptionListUpdateCommand } from './SendPrescriptionAction.hooks';
import { SendPrescriptionErrorStatus } from './SendPrescriptionErrorStatus';
import { MedicineParametersChangedShowModalEventInterceptor } from './standardPrescription/medicineParametersChanged/MedicineParametersChangedShowModalEventInterceptor';
import { usePatientInPersonExaminationRequiredModalOpener } from './standardPrescription/patientInPersonExaminationRequired/PatientInPersonExaminationRequiredModal/PatientInPersonExaminationRequiredModal.hooks';

type SendPrescriptionActionChildrenRenderProps = {
   readonly sendPrescriptions: PromiseVoidFunction;
   readonly disabled: boolean;
};

type SendPrescriptionActionProps = Pick<DraftPrescriptionsFormController, 'handleSubmit' | 'trigger' | 'control'> & {
   readonly visitId: VisitId;
   readonly documentationScope: Visit['documentationScope'];
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly patientId: PatientId;
   readonly onPrescriptionsSent: OnPrescriptionsSent;
   readonly sentPrescriptionPackages: SentPrescriptionPackage[];
   readonly children: (renderProps: SendPrescriptionActionChildrenRenderProps) => ReactElement;
};

export const SendPrescriptionAction = (props: SendPrescriptionActionProps): JSX.Element => {
   const {
      openProAuctoreAndProFamiliaMissingContactDetailsModal,
      renderProAuctoreAndProFamiliaMissingContactDetailsModal,
   } = useProAuctoreAndProFamiliaMissingContactDetailsModalOpener();
   const {
      openPatientInPersonExaminationRequiredModal,
      renderPatientInPersonExaminationRequiredModal,
   } = usePatientInPersonExaminationRequiredModalOpener();
   const {
      openPatientPrescriptionsHistoryDownloadVerificationModal,
      renderPatientPrescriptionsHistoryDownloadVerificationModal,
   } = usePatientPrescriptionsHistoryDownloadVerificationModal();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();
   const { executeAsync: sendPrescriptions, error: sendPrescriptionFault } = useCqrsVisitPrescriptionListUpdateCommand({
      onPrescriptionsSent: props.onPrescriptionsSent,
      onAuthenticationRequiredFault: openAccountAuthorizationModal,
      onHistoricalPrescriptionNotCheckedFault: openPatientPrescriptionsHistoryDownloadVerificationModal,
      onMissingDoctorContactDetails: openProAuctoreAndProFamiliaMissingContactDetailsModal,
      onPatientInPersonExaminationRequired: openPatientInPersonExaminationRequiredModal,
   });
   const draftPrescriptions = useDraftPrescriptionsWatch(props.control);

   const handleClick = props.handleSubmit(async (formData) => {
      if (!formData.draftPrescriptions.length) {
         props.onPrescriptionsSent([]);
         return;
      }

      // eslint-disable-next-line no-return-await
      await sendPrescriptions({
         visitId: props.visitId,
         sentPrescriptionPackages: props.sentPrescriptionPackages,
         draftPrescriptions: formData.draftPrescriptions,
         prescriptionCategory: formData.prescriptionCategory,
      });
   });

   return (
      <>
         <ActionBar.Portal elementType={ActionBarElementType.PRIMARY}>
            <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
               {(waiting) => (
                  <ValidateFormEventInterceptor trigger={props.trigger}>
                     <NfzDepartmentUpdateEventInterceptor draftPrescriptions={draftPrescriptions}>
                        <PatientEntitlementsEventInterceptor
                           draftPrescriptions={draftPrescriptions}
                           patientId={props.patientId}
                           confirmationButtonLabel={draftPrescriptionsTranslationKeys.saveAndSend}
                        >
                           <PatientEntitlementBenefitsCheckEventInterceptor
                              visitId={props.visitId}
                              draftPrescriptions={draftPrescriptions}
                           >
                              <PatientReimbursementIndicationsCheckEventInterceptor
                                 draftPrescriptions={draftPrescriptions}
                                 visitDiagnoses={props.visitDiagnoses}
                              >
                                 <MedicineParametersChangedShowModalEventInterceptor
                                    draftPrescriptions={draftPrescriptions}
                                 >
                                    {props.children({
                                       sendPrescriptions: handleClick,
                                       disabled: !draftPrescriptions.length || waiting,
                                    })}
                                 </MedicineParametersChangedShowModalEventInterceptor>
                              </PatientReimbursementIndicationsCheckEventInterceptor>
                           </PatientEntitlementBenefitsCheckEventInterceptor>
                        </PatientEntitlementsEventInterceptor>
                     </NfzDepartmentUpdateEventInterceptor>
                  </ValidateFormEventInterceptor>
               )}
            </DataSyncWaitingEventInterceptor>
         </ActionBar.Portal>

         {renderAccountAuthorizationnModal({
            onAuthorization: handleClick,
         })}

         {renderPatientPrescriptionsHistoryDownloadVerificationModal({
            visitId: props.visitId,
            documentationScope: props.documentationScope,
         })}

         {renderProAuctoreAndProFamiliaMissingContactDetailsModal({
            onMissingDetailsUpdated: handleClick,
         })}

         {renderPatientInPersonExaminationRequiredModal({
            onPatientInPersonExaminationConfirmed: handleClick,
            visitId: props.visitId,
         })}

         {isFault(sendPrescriptionFault) && (
            <DraftPrescriptionsStatusPanelPortal>
               <SendPrescriptionErrorStatus fault={sendPrescriptionFault} />
            </DraftPrescriptionsStatusPanelPortal>
         )}
      </>
   );
};
